/**
 * Enregistre le SW en production uniquement (évite les conflits de cache en dev).
 * Tester l’installation : `npm run build` puis servir le dossier `build` en HTTPS ou `npx serve -s build`.
 */
export function registerServiceWorker() {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
    return;
  }

  const swUrl = `${process.env.PUBLIC_URL || ''}/sw.js`;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register(swUrl).catch(() => {});
  });
}

const CONTROLLER_CHANGE_TIMEOUT_MS = 8000;

function waitForControllerChange() {
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timeoutId);
      navigator.serviceWorker.removeEventListener('controllerchange', finish);
      resolve();
    };
    const timeoutId = setTimeout(finish, CONTROLLER_CHANGE_TIMEOUT_MS);
    navigator.serviceWorker.addEventListener('controllerchange', finish);
  });
}

function waitUntilInstalled(worker) {
  if (!worker || worker.state !== 'installing') return Promise.resolve();
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timeoutId);
      worker.removeEventListener('statechange', onStateChange);
      resolve();
    };
    const onStateChange = () => {
      if (worker.state === 'installed' || worker.state === 'activated' || worker.state === 'redundant') {
        finish();
      }
    };
    const timeoutId = setTimeout(finish, CONTROLLER_CHANGE_TIMEOUT_MS);
    worker.addEventListener('statechange', onStateChange);
  });
}

export async function applyAppUpdate() {
  if (!('serviceWorker' in navigator)) return;

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;

  try {
    await registration.update();
  } catch {
    // Continue with the worker already registered.
  }

  const incoming = registration.waiting || registration.installing;
  await waitUntilInstalled(incoming);
  const waiting = registration.waiting;
  if (!waiting) return;

  // Garder le cache du nouveau worker. Le supprimer ici force le
  // rechargement à réutiliser un index.html périmé, et le bouton revient.
  const controllerChange = waitForControllerChange();
  waiting.postMessage({ type: 'SKIP_WAITING' });
  await controllerChange;
}

export async function ensureOfflineShell() {
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) throw new Error('OFFLINE_BROWSER_UNSUPPORTED');
  const registration = await navigator.serviceWorker.ready;
  const worker = registration.active;
  if (!worker) throw new Error('OFFLINE_SHELL_NOT_READY');
  await new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const timeout = setTimeout(() => { channel.port1.close(); reject(new Error('Rechargez Node en ligne pour préparer cette version.')); }, 15000);
    channel.port1.onmessage = ({ data }) => {
      clearTimeout(timeout); channel.port1.close();
      if (data.ready) resolve(); else reject(new Error('OFFLINE_SHELL_NOT_READY'));
    };
    worker.postMessage({ type: 'NODE_OFFLINE_READY' }, [channel.port2]);
  });
}
